/**
 * P-4 添付・OCR確認 (§14-3-B)。P-3 からのモーダル/サブ画面 (独立ルート、モーダルオーバーレイの見た目)。
 * 写真アップロード → 同期OCR → 確認/取り直しの1サイクルを1画面に留める。
 *
 * ⚠️ スコープ限定 (次セッションが「不足」と誤認しないこと):
 * ⓪ 2026-09-09 (user Q3、必要枚数対応): 従来は「1項目=写真1枚 (差し替え)」前提で、
 *    このページは常に「その項目の最新の添付」を表示していた。複数枚必要な項目 (§14-5-6
 *    `required_count`) に対応するため、クエリパラメータ `?attachment_id=` の有無で
 *    「既存の特定の添付を確認・取り直しする」(指定あり) か「新規に1枚追加する」(指定なし、
 *    既存の添付があっても常にStep Aから始める) かを分岐する。P-3 (DailyReportDetailPage.tsx)
 *    が遷移先を使い分ける。
 * ① OCR結果の「あなたの入力」欄 (§14-3-B Step C-ok のワイヤーが示す、日報側フィールドと同じ値を
 *    ここでも直接編集できるという構想) は実装していない。value_kind='attachment' の項目と
 *    OCR合計金額を突き合わせる先の money フィールドとの正式な対応関係がバックエンドに存在しない
 *    ため (突合は `ocr:{attachment_kind}` という attachment_kind 単位の粗い参照であり、
 *    特定の field_definition_id とは結びつかない)。Step C-ok は OCR結果を読み取り専用の
 *    参考情報として表示し、「日報に戻って該当項目に入力してください」と案内するに留める。
 * ② `attachment_kind` の決定は `field_options_json.attachment_kind` を読む緩い規約
 *    (`inferAttachmentKind`、daily-report-api.ts 参照) で、未設定時は 'other' にフォールバックする。
 *    正式な設定UIはP-5実装時に追加すること。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, ZoomIn } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { FilePickerField } from '@/components/FilePickerField';
import { toast } from '@/stores/toast';
import { ApiClientError } from '@/lib/api';
import {
  fetchDailyReportDetail, fetchDailyReportAttachments, fetchDailyReportAttachmentBlob,
  uploadDailyReportAttachment, reocrDailyReportAttachment, confirmDailyReportOcr, inferAttachmentKind,
  type DailyReportAttachment,
} from '@/lib/daily-report-api';
import { inputCls } from './DailyReportFieldRow';

function AttachmentImage({ dailyReportId, attachmentId, onZoom }: { dailyReportId: string; attachmentId: string; onZoom?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let revoked = '';
    let cancelled = false;
    (async () => {
      try {
        const blob = await fetchDailyReportAttachmentBlob(dailyReportId, attachmentId);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoked = url;
        setSrc(url);
      } catch {
        // 画像取得失敗は無音 (OCR結果自体は別途表示されるため、写真だけ出せなくても致命的ではない)
      }
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [dailyReportId, attachmentId]);

  if (!src) {
    return <div className="flex items-center justify-center h-40 bg-surface-sunken rounded text-text-sub"><Loader2 className="w-5 h-5 animate-spin" aria-hidden /></div>;
  }
  const img = <img src={src} alt="添付写真" className="w-full max-h-72 object-contain rounded border border-surface-border" />;
  // onZoom 未指定 (ズームモーダル内で自分自身を表示する場合) は、押しても何も起きない
  // ボタン+「タップで全画面ズーム」表記を出さない。
  if (!onZoom) return img;
  return (
    <button type="button" className="block w-full" onClick={onZoom} aria-label="写真を全画面表示">
      {img}
      <span className="mt-1 text-caption text-text-sub flex items-center gap-1 justify-center"><ZoomIn size={12} aria-hidden />タップで全画面ズーム</span>
    </button>
  );
}

export function DailyReportAttachmentPage() {
  const { id, fieldDefinitionId } = useParams<{ id: string; fieldDefinitionId: string }>();
  const [searchParams] = useSearchParams();
  // ⓪ Q3対応: 指定が無ければ (P-3の「写真を追加」) 既存の添付があっても常に新規追加として扱う。
  const targetAttachmentId = searchParams.get('attachment_id');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const detailQuery = useQuery({ queryKey: ['daily-report-detail', id], queryFn: () => fetchDailyReportDetail(id!), enabled: !!id });
  const attachmentsQuery = useQuery({
    queryKey: ['daily-report-attachments', id],
    queryFn: () => fetchDailyReportAttachments(id!),
    enabled: !!id,
  });

  const [uploading, setUploading] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [zoomOpen, setZoomOpen] = useState(false);
  const [localAttachment, setLocalAttachment] = useState<DailyReportAttachment | null>(null);

  const field = detailQuery.data?.fields.find((f) => f.field_definition_id === fieldDefinitionId);
  // ⓪ Q3対応: attachment_id 指定時のみ、その特定の添付 (確認・取り直し対象) を表示する。
  // 未指定時は既存添付の有無に関わらず null (=Step A、新規の1枚として追加する)。
  const serverAttachment = targetAttachmentId
    ? attachmentsQuery.data?.find((a) => a.attachment_id === targetAttachmentId) ?? null
    : null;
  const attachment = localAttachment ?? serverAttachment;
  const isDraft = detailQuery.data?.daily_report.status === 'draft';

  // ⚠️ P-3 の daily-report-detail クエリは staleTime:Infinity (編集中のローカル state を
  // バックグラウンド再取得で上書きしないため) のため、P-4 でのアップロード/確認操作の結果を
  // P-3 へ持ち帰るには、戻る前にこのタブ内でキャッシュを更新しておく必要がある
  // (P-3側は「1回だけ hydrate」するため、古いキャッシュのまま遷移すると反映されない)。
  const goBack = async () => {
    if (id) await qc.invalidateQueries({ queryKey: ['daily-report-detail', id] });
    navigate(`/daily-reports/${id}`);
  };

  const startUpload = async (file: File) => {
    if (!id || !fieldDefinitionId) return;
    setUploading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (attachment) {
        const ocr = await reocrDailyReportAttachment(id, attachment.attachment_id, file, controller.signal);
        setLocalAttachment({ ...attachment, ocr, display_level: 'full' });
      } else {
        const kind = inferAttachmentKind(field?.field_options_json ?? null);
        const uploaded = await uploadDailyReportAttachment(id, fieldDefinitionId, kind, file, controller.signal);
        setLocalAttachment(uploaded);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // 中止導線: 通信中断後、実際にサーバー側で成功していないかを再フェッチで確認する
        // (§14-3-B「中止したのに実は成功していた、を隠さない」)。
        // ⚠️ design-and-codeレビューHIGH是正 (2026-09-09): 2回目以降のreocr中止では
        // localAttachment に古いOCR結果が残ったままで refetch 結果が無視されていた
        // (attachment = localAttachment ?? serverAttachment のため localAttachment 優先)。
        // 再フェッチ後は必ず localAttachment をクリアし、サーバーの実際の状態
        // (serverAttachment) を表示させる。
        await attachmentsQuery.refetch();
        setLocalAttachment(null);
        toast.info('アップロードを中止しました');
      } else {
        toast.error(err instanceof ApiClientError ? err.message : 'アップロードに失敗しました');
      }
    } finally {
      setUploading(false);
      abortRef.current = null;
    }
  };

  const cancelUpload = () => abortRef.current?.abort();

  const doConfirm = async (reason?: string) => {
    if (!id || !attachment) return;
    try {
      const ocr = await confirmDailyReportOcr(id, attachment.attachment_id, reason);
      setLocalAttachment({ ...attachment, ocr });
      toast.success('確認済みにしました');
      void goBack();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : '確認に失敗しました');
    }
  };

  if (!id || !fieldDefinitionId) return <p className="text-body text-danger-text">パラメータが不正です。</p>;
  if (detailQuery.isLoading || attachmentsQuery.isLoading) {
    return <div className="flex items-center justify-center gap-2 text-text-sub py-16"><Loader2 className="w-5 h-5 animate-spin" aria-hidden /><span className="text-body">読み込んでいます…</span></div>;
  }

  const ocr = attachment?.ocr ?? null;

  return (
    <div className="max-w-md mx-auto">
      <div className="sticky top-0 z-10 bg-surface-canvas/95 backdrop-blur-sm pt-2 pb-3 -mx-4 px-4 border-b border-surface-line flex items-center justify-between">
        <button type="button" className="text-body text-chrome-base underline underline-offset-2 min-h-[44px] px-1 -ml-1 inline-flex items-center" onClick={goBack}>
          ← 日報に戻る
        </button>
        <h1 className="text-label text-text-sub">{field?.label ?? '添付'}</h1>
      </div>

      {!isDraft && (
        <p className="mt-3 text-body text-text-sub">提出済みの日報のため、添付の変更・取り直しはできません。</p>
      )}

      <div className="mt-4">
        {uploading ? (
          // Step B: アップロード中・OCR実行中
          <div className="space-y-3">
            <div className="flex items-center justify-center h-40 bg-surface-sunken rounded"><Loader2 className="w-6 h-6 animate-spin text-text-sub" aria-hidden /></div>
            <div className="flex items-center justify-between">
              <span className="text-body text-text-sub flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" aria-hidden />読み取り中…</span>
              <Button variant="secondary" size="sm" onClick={cancelUpload}>中止</Button>
            </div>
          </div>
        ) : !attachment ? (
          // Step A: 未添付
          isDraft && (
            <FilePickerField
              label={field?.label ?? '写真'} accept="image/jpeg,image/png" capture="environment" preview
              onChange={() => {}}
              onCapture={(file) => { void startUpload(file); }}
            />
          )
        ) : ocr === null || ocr === undefined ? (
          <p className="text-body text-text-sub">OCR結果を取得できませんでした。差し替えてください。</p>
        ) : ocr.confirmed_at ? (
          // 確認済み (§4-2-a: status='ok' に限らず、読めないまま進める判断をした場合も confirmed_at が
          // 付く。ステータスに関わらず「確認済み」表示を優先する — design-and-codeレビュー是正、
          // 2026-09-09。以前は status!=='ok' の分岐が confirmed_at を一切見ておらず、確認済みの
          // 添付を差し戻す度に再度理由入力を求めてしまっていた)
          <div className="space-y-3">
            <AttachmentImage dailyReportId={id} attachmentId={attachment.attachment_id} onZoom={() => setZoomOpen(true)} />
            {ocr.status === 'ok' && (
              <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
                <p className="text-caption text-text-sub">読み取り結果</p>
                <div className="flex items-center justify-between">
                  <span className="text-body text-text-sub">合計金額</span>
                  <span className="text-h3 text-text-main">{ocr.total_amount !== null ? `¥${ocr.total_amount.toLocaleString()}` : '—'}</span>
                </div>
                {ocr.total_count !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-body text-text-sub">枚数</span>
                    <span className="text-h3 text-text-main">{ocr.total_count.toLocaleString()}枚</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-body text-success-text flex items-center gap-1.5"><CheckCircle2 size={16} aria-hidden />確認済みです</p>
            {isDraft && (
              <FilePickerField label="別の写真に差し替える" accept="image/jpeg,image/png" capture="environment"
                onChange={() => {}} onCapture={(file) => { void startUpload(file); }} />
            )}
          </div>
        ) : ocr.status === 'ok' ? (
          // Step C-ok (未確認)
          <div className="space-y-3">
            <AttachmentImage dailyReportId={id} attachmentId={attachment.attachment_id} onZoom={() => setZoomOpen(true)} />
            <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
              <p className="text-caption text-text-sub">読み取り結果</p>
              <div className="flex items-center justify-between">
                <span className="text-body text-text-sub">合計金額</span>
                <span className="text-h3 text-text-main">{ocr.total_amount !== null ? `¥${ocr.total_amount.toLocaleString()}` : '—'}</span>
              </div>
              {ocr.total_count !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-body text-text-sub">枚数</span>
                  <span className="text-h3 text-text-main">{ocr.total_count.toLocaleString()}枚</span>
                </div>
              )}
              <p className="text-caption text-text-sub">この金額を参考に、日報の該当項目へ入力してください。</p>
            </div>
            {isDraft && (
              <Button variant="primary" className="w-full" onClick={() => { void doConfirm(); }}>この写真を確認済みにする</Button>
            )}
            {isDraft && (
              <FilePickerField label="別の写真に差し替える" accept="image/jpeg,image/png" capture="environment"
                onChange={() => {}} onCapture={(file) => { void startUpload(file); }} />
            )}
          </div>
        ) : !ocr.retake_exhausted ? (
          // Step C-unreadable-1回目
          <div className="space-y-3">
            <AttachmentImage dailyReportId={id} attachmentId={attachment.attachment_id} onZoom={() => setZoomOpen(true)} />
            <p className="text-body text-alert-text flex items-center gap-1.5"><AlertTriangle size={16} aria-hidden />読み取れませんでした{ocr.unreadable_reason ? `（${ocr.unreadable_reason}）` : ''}</p>
            {isDraft && (
              <FilePickerField label="撮り直す" accept="image/jpeg,image/png" capture="environment"
                onChange={() => {}} onCapture={(file) => { void startUpload(file); }} />
            )}
          </div>
        ) : (
          // Step C-unreadable-2回目 (例外脱出、§4-2-a)
          <div className="space-y-3">
            <AttachmentImage dailyReportId={id} attachmentId={attachment.attachment_id} onZoom={() => setZoomOpen(true)} />
            <p className="text-body text-danger-text flex items-center gap-1.5"><AlertTriangle size={16} aria-hidden />2回続けて読み取れませんでした</p>
            {isDraft && (
              <>
                <FilePickerField label="撮り直す" accept="image/jpeg,image/png" capture="environment"
                  onChange={() => {}} onCapture={(file) => { void startUpload(file); }} />
                <div className="border-t border-surface-line pt-3">
                  <label className="text-label text-text-sub" htmlFor="drattach-reason">読めないまま添付して進める理由<span className="ml-0.5">*</span></label>
                  <textarea id="drattach-reason" className={`${inputCls} h-24 mt-1`} value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
                  <Button variant="secondary" className="w-full mt-2"
                    disabled={!reasonText.trim()}
                    onClick={() => { void doConfirm(reasonText.trim()); }}>
                    読めないまま添付して進める
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* design-expert Major是正 (2026-09-09): 独自オーバーレイ (Escape/フォーカストラップ/
          scroll-lock/modal-stack 登録が無かった) から既存 Modal.tsx へ置き換える。 */}
      {attachment && (
        <Modal open={zoomOpen} title="添付写真" onClose={() => setZoomOpen(false)} size="lg">
          <AttachmentImage dailyReportId={id} attachmentId={attachment.attachment_id} />
        </Modal>
      )}
    </div>
  );
}
